<template>
  <multiselect
    ref="ms"
    v-bind="$attrs"
    :options="sortedOptions"
    :label="label"
    :custom-label="customLabel"
    :group-values="groupValues"
  >
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData || {}" />
    </template>
  </multiselect>
</template>

<script>
// Drop-in replacement for `vue-multiselect` that alphabetizes `options`.
// Pass `:sort="false"` for lists with a natural order (months, weekdays...).
import Multiselect from "vue-multiselect";
import { sortOptions } from "@/utils/sortOptions";

export default {
  name: "SortedMultiselect",

  components: { Multiselect },

  inheritAttrs: false,

  props: {
    options: { type: Array, default: () => [] },
    sort: { type: Boolean, default: true },
    // Declared so we can read them for sorting; forwarded to vue-multiselect
    // unchanged (undefined falls back to its own defaults).
    label: { type: String, default: undefined },
    customLabel: { type: Function, default: undefined },
    groupValues: { type: String, default: undefined },
  },

  computed: {
    sortedOptions() {
      if (!this.sort || !Array.isArray(this.options)) return this.options;

      const getLabel = (option) =>
        this.customLabel
          ? this.customLabel(option, this.label)
          : option !== null && typeof option === "object"
            ? option[this.label]
            : option;

      if (this.groupValues) {
        return this.options.map((group) => ({
          ...group,
          [this.groupValues]: sortOptions(group[this.groupValues], getLabel),
        }));
      }
      return sortOptions(this.options, getLabel);
    },

    // Some parents reach into the ref to clear the search box / close the list.
    search: {
      get() {
        return this.$refs.ms?.search;
      },
      set(value) {
        if (this.$refs.ms) this.$refs.ms.search = value;
      },
    },
    isOpen() {
      return this.$refs.ms?.isOpen;
    },
  },

  methods: {
    activate() {
      this.$refs.ms?.activate();
    },
    deactivate() {
      this.$refs.ms?.deactivate();
    },
    toggle() {
      this.$refs.ms?.toggle();
    },
  },
};
</script>
